import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSelect,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { updateItem, ItemRequestStatus, getItem } from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert, successAlert, warningAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import Document, { newDocument } from "../../models/document";
import Subcompany from "../../models/subcompany";
import Datetime from "react-datetime";
import moment from "moment";
import SubcompanySelect from "../subcompany/SubcompanySelect";
import Asset from "../../models/asset";
import AssetSelect from "../asset/AssetSelect";
import DocumentCategory from "../../models/document-category";
import DocumentCategorySelect from "../document-category/DocumentCategorySelect";
import DocumentSubategory from "../../models/document-subcategory";
import DocumentSubcategorySelect from "../document-subcategory/DocumentSubcategorySelect";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";
import "react-datetime/css/react-datetime.css";
import { useDropzone } from "react-dropzone/.";
import Tag from "../../models/tag";
import { getAbsoluteUrl } from "../../api/urls";
import { fetchAuthenticated } from "../../api/utils";
import TagsCell from "./TagsCell";

interface DocumentFormProps {
  initialDocument?: Document;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const DocumentForm: React.FC<DocumentFormProps> = ({
  initialDocument,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingDocument, setEditingDocument] = useState<Document>(
    initialDocument ? initialDocument : newDocument()
  );
  const [selectedSubcompany, setSelectedSubcompany] = useState<
    Subcompany | undefined
  >(undefined);

  const [selectedAsset, setSelectedAsset] = useState<Asset | undefined>(
    undefined
  );

  const [selectedDocumentCategory, setSelectedDocumentCategory] = useState<
    DocumentCategory | undefined
  >(undefined);

  const [selectedDocumentSubCategory, setSelectedDocumentSubCategory] =
    useState<DocumentCategory | undefined>(undefined);

  const [expiryDate, setExpiryDate] = useState<string | moment.Moment>("");

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const [tags, setTags] = useState<Tag[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);

  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [, setDownloading] = useState(false);

  const handleDownloadClick = async () => {
    if (!initialDocument?.id) return;

    try {
      setDownloading(true);
      const downloadUrl = getAbsoluteUrl(
        `/documents/${initialDocument.id}/get_document_url/`
      );

      const downloadResponse = await fetchAuthenticated(downloadUrl, {
        method: "GET",
      });

      if (!downloadResponse.ok) {
        throw new Error(
          `Error HTTP ${downloadResponse.status}: ${downloadResponse.statusText}`
        );
      }

      const blob = await downloadResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = initialDocument.filePath || "documento";

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al descargar:", error);
      errorAlert(
        `Error al descargar: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    } finally {
      setDownloading(false);
    }
  };

  const onExpiryDatehange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) {
      return;
    }
    setExpiryDate(value);
    if (moment.isMoment(value)) {
      setEditingDocument({
        ...editingDocument,
        expirationDate: value.toISOString(),
      });
    } else if (value !== undefined && value !== "") {
      setEditingDocument({
        ...editingDocument,
        expirationDate: value,
      });
    }
  };

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocument({
      ...editingDocument,
      name: e.target.value,
    });
  };

  const onVersionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocument({
      ...editingDocument,
      version: e.target.value,
    });
  };

  const onTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocument({
      ...editingDocument,
      documentType: e.target.value,
    });
  };

  const onSubcompanyChange = (newSubcompany: Subcompany | null) => {
    setSelectedSubcompany(newSubcompany !== null ? newSubcompany : undefined);
    setEditingDocument({
      ...editingDocument,
      subcompanyId: newSubcompany !== null ? newSubcompany.id : undefined,
    });
  };

  const onAssetChange = (newAsset: Asset | null) => {
    setSelectedAsset(newAsset !== null ? newAsset : undefined);
    setEditingDocument({
      ...editingDocument,
      assetId: newAsset !== null ? newAsset.id : undefined,
    });
  };

  const onDocumentCategoryChange = (
    newDocumentCategory: DocumentCategory | null
  ) => {
    setSelectedDocumentCategory(
      newDocumentCategory !== null ? newDocumentCategory : undefined
    );
    setEditingDocument({
      ...editingDocument,
      documentCategoryId:
        newDocumentCategory !== null ? newDocumentCategory.id : undefined,
    });
  };

  const onDocumentSubCategoryChange = (
    newDocumentSubCategory: DocumentCategory | null
  ) => {
    setSelectedDocumentSubCategory(
      newDocumentSubCategory !== null ? newDocumentSubCategory : undefined
    );
    setEditingDocument({
      ...editingDocument,
      documentSubcategoryId:
        newDocumentSubCategory !== null ? newDocumentSubCategory.id : undefined,
    });
  };

  const onPhysicalRequiredChange = (newPhysicalRequired: boolean) => {
    setEditingDocument({
      ...editingDocument,
      physicalCopyRequired: newPhysicalRequired,
    });
  };

  const onNoticeDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocument((editingItem) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseInt(e.target.value))) {
        amount = parseInt(e.target.value);
      }

      return { ...editingItem, expirationNoticeDays: amount };
    });
  };

  const onGraceDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocument((editingItem) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseInt(e.target.value))) {
        amount = parseInt(e.target.value);
      }

      return { ...editingItem, expirationGraceDays: amount };
    });
  };

  const onRepeatDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocument((editingItem) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseInt(e.target.value))) {
        amount = parseInt(e.target.value);
      }

      return { ...editingItem, expirationReminderRepeatDays: amount };
    });
  };

  const handleFileUpload = async (
    file: File,
    documentData: Document
  ): Promise<boolean> => {
    try {
      if (!file) throw new Error("No se seleccionó ningún archivo");

      const formData = new FormData();

      if (documentData.id !== undefined) {
        formData.append("document_id", String(documentData.id));
      }

      formData.append("file", file);
      formData.append("name", documentData.name || file.name);
      formData.append("version", documentData.version || "1");
      formData.append("company_id", String(company.id));
      formData.append("file_path", file.name);
      formData.append("asset_id", String(documentData.assetId ?? ""));
      formData.append("document_type", documentData.documentType || "digital");
      formData.append(
        "physical_copy_required",
        String(documentData.physicalCopyRequired || false)
      );
      formData.append(
        "expiration_notice_days",
        String(documentData.expirationNoticeDays || 30)
      );
      formData.append(
        "expiration_grace_days",
        String(documentData.expirationGraceDays || 15)
      );
      formData.append(
        "expiration_reminder_repeat_days",
        String(documentData.expirationReminderRepeatDays || 7)
      );

      if (
        documentData.documentCategoryId !== null &&
        documentData.documentCategoryId !== undefined
      ) {
        formData.append(
          "document_category_id",
          String(documentData.documentCategoryId)
        );
      }
      if (
        documentData.documentSubcategoryId !== null &&
        documentData.documentSubcategoryId !== undefined
      ) {
        formData.append(
          "document_subcategory_id",
          String(documentData.documentSubcategoryId)
        );
      }
      if (
        documentData.subcompanyId !== null &&
        documentData.subcompanyId !== undefined
      ) {
        formData.append("subcompany_id", String(documentData.subcompanyId));
      }
      if (documentData.expirationDate) {
        formData.append("expiration_date", documentData.expirationDate);
      }

      const uploadUrl = getAbsoluteUrl("/documents/create_document_url/");
      const uploadResponse = await fetchAuthenticated(uploadUrl, {
        method: "POST",
        body: formData,
      });

      const result = await uploadResponse.json();

      if (!uploadResponse.ok || result.status !== SUCCESS) {
        throw new Error(result.detail || "Error al subir el archivo");
      }

      return true;
    } catch (error) {
      console.error("Error en handleFileUpload:", error);
      errorAlert(
        `Error al subir archivo: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
      return false;
    }
  };

  const onSave = useCallback(async () => {
    setSubmitting(true);

    try {
      // Validar campos requeridos
      if (!editingDocument.name || !editingDocument.version) {
        errorAlert("Por favor complete los campos obligatorios");
        setSubmitting(false);
        return;
      }

      let toSendDocument: Document = {
        ...editingDocument,
        companyId: company.id,
        documentType: editingDocument.documentType || "digital",
        filePath: uploadedFile ? uploadedFile.name : editingDocument.filePath,
        physicalCopyRequired: editingDocument.physicalCopyRequired || false,
        tags: tags, // Incluir tags
      };

      let requestPromise: Promise<ItemRequestStatus<Document>>;

      if (editingDocument.id === undefined) {
        // CREATE: Si hay archivo, usar el endpoint de carga
        if (uploadedFile) {
          const uploadSuccess = await handleFileUpload(
            uploadedFile,
            toSendDocument
          );
          if (!uploadSuccess) {
            setSubmitting(false);
            return;
          }
          successAlert("Documento creado exitosamente!");
          setErrors({});
          clearForm();
          onSuccess();
        } else {
          errorAlert("Debe seleccionar un archivo para crear el documento");
        }
      } else {
        // UPDATE: Si hay nuevo archivo, subirlo primero
        if (uploadedFile) {
          const uploadSuccess = await handleFileUpload(
            uploadedFile,
            toSendDocument
          );
          if (!uploadSuccess) {
            setSubmitting(false);
            return;
          }
        }

        // Luego actualizar el documento
        requestPromise = updateItem<Document>(
          `/documents/${toSendDocument.id}/`,
          toSendDocument
        );

        const documentStatus = await requestPromise;

        if (documentStatus.status !== SUCCESS) {
          if (documentStatus.errors !== undefined) {
            setErrors(documentStatus.errors);
          }

          let message = "Ha ocurrido un error!!";
          if (documentStatus.detail !== undefined) {
            message = documentStatus.detail;
          }
          errorAlert(message);
        } else {
          setErrors({});
          successAlert("Documento actualizado exitosamente!");
          clearForm();
          onSuccess();
        }
      }
    } catch (error) {
      console.error("Error al guardar:", error);
      errorAlert(
        `Error al guardar: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingDocument, company, uploadedFile, tags, onSuccess]);

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingDocument(newDocument());
    setExpiryDate("");
    setSelectedAsset(undefined);
    setSelectedSubcompany(undefined);
    setSelectedDocumentCategory(undefined);
    setSelectedDocumentSubCategory(undefined);
    setUploadedFile(null);
    setTags([]);
  };

  const fetchSubCompany = useCallback(
    async (id: number) => {
      const typeStatus = await getItem<Subcompany>(`/subcompanies/${id}/`);
      if (typeStatus.status === SUCCESS) {
        if (typeStatus.data !== undefined) {
          setSelectedSubcompany(typeStatus.data);
        }
      } else {
        const message = typeStatus.detail
          ? typeStatus.detail
          : "Error desconocido";
        warningAlert(message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialDocument]
  );

  const fetchAsset = useCallback(
    async (id: number) => {
      const companyId = company.id;

      const typeStatus = await getItem<Asset>(
        `/assets/${id}/`,
        new Map<string, string>([["company_id", companyId!.toString()]])
      );
      if (typeStatus.status === SUCCESS) {
        if (typeStatus.data !== undefined) {
          setSelectedAsset(typeStatus.data);
        }
      } else {
        const message = typeStatus.detail
          ? typeStatus.detail
          : "Error desconocido";
        warningAlert(message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialDocument, company]
  );

  const fetchDocumentCategory = useCallback(
    async (id: number) => {
      const companyId = company.id;
      const categoryStatus = await getItem<DocumentCategory>(
        `/document_categories/${id}/`,
        new Map<string, string>([["company_id", companyId!.toString()]])
      );
      if (categoryStatus.status === SUCCESS) {
        if (categoryStatus.data !== undefined) {
          setSelectedDocumentCategory(categoryStatus.data);
        }
      } else {
        const message = categoryStatus.detail
          ? categoryStatus.detail
          : "Error desconocido";
        warningAlert(message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialDocument, company]
  );

  const fetchDocumentSubCategory = useCallback(
    async (id: number) => {
      const companyId = company.id;
      const categoryStatus = await getItem<DocumentSubategory>(
        `/document_subcategories/${id}/`,
        new Map<string, string>([["company_id", companyId!.toString()]])
      );
      if (categoryStatus.status === SUCCESS) {
        if (categoryStatus.data !== undefined) {
          setSelectedDocumentSubCategory(categoryStatus.data);
        }
      } else {
        const message = categoryStatus.detail
          ? categoryStatus.detail
          : "Error desconocido";
        warningAlert(message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialDocument, company]
  );

  const fetchTags = useCallback(async () => {
    let additionalParams: Map<string, string> = new Map<string, string>();
    additionalParams.set("company_id", company.id!.toString());

    const tagsStatus = await getItem<{ items: Tag[] }>(
      "/tags/",
      additionalParams
    );

    if (tagsStatus.status === SUCCESS && tagsStatus.data) {
      setAllTags(tagsStatus.data.items || []);
    }
  }, [company.id]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        console.log("File dropped:", acceptedFiles[0]);
        setUploadedFile(acceptedFiles[0]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingDocument]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  const handleTagsChange = (selectedTags: Tag[]) => {
    setTags(selectedTags);
  };

  useEffect(() => {
    setEditingDocument(initialDocument ? initialDocument : newDocument());
    if (initialDocument?.subcompanyId) {
      fetchSubCompany(initialDocument.subcompanyId);
    }
    if (initialDocument?.assetId) {
      fetchAsset(initialDocument.assetId);
    }
    if (initialDocument?.documentCategoryId) {
      fetchDocumentCategory(initialDocument.documentCategoryId);
    }
    if (initialDocument?.documentSubcategoryId) {
      fetchDocumentSubCategory(initialDocument.documentSubcategoryId);
    }
    const date = initialDocument?.expirationDate;
    setExpiryDate(date ? moment(date) : "");

    setTags(initialDocument?.tags || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDocument]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nombre:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingDocument.name)}
                onChange={onNameChange}
                placeholder="Nombre"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("name", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Versión:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingDocument.version)}
                onChange={onVersionChange}
                placeholder="Version"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("version", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Sub Organización:</CLabel>
            </CCol>
            <CCol>
              <SubcompanySelect
                value={selectedSubcompany ? selectedSubcompany : null}
                onChange={onSubcompanyChange}
              ></SubcompanySelect>
              <FieldErrors
                errors={getFieldErrors("subcompanyId", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Productor:</CLabel>
            </CCol>
            <CCol>
              <AssetSelect
                value={selectedAsset ? selectedAsset : null}
                onChange={onAssetChange}
              ></AssetSelect>
              <FieldErrors
                errors={getFieldErrors("assetId", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>

        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Categoría de Documento:</CLabel>
            </CCol>
            <CCol>
              <DocumentCategorySelect
                value={
                  selectedDocumentCategory ? selectedDocumentCategory : null
                }
                onChange={onDocumentCategoryChange}
              ></DocumentCategorySelect>
              <FieldErrors
                errors={getFieldErrors("categoryId", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Sub Categoría de Documento:</CLabel>
            </CCol>
            <CCol>
              <DocumentSubcategorySelect
                value={
                  selectedDocumentSubCategory
                    ? selectedDocumentSubCategory
                    : null
                }
                onChange={onDocumentSubCategoryChange}
              ></DocumentSubcategorySelect>
              <FieldErrors
                errors={getFieldErrors("subcategoryId", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Tipo de Documento:</CLabel>
            </CCol>
            <CCol>
              <CSelect
                type="text"
                defaultValue={"digital"}
                onChange={onTypeChange}
              >
                <option value={"digital"}>Digital</option>
                <option value={"physical"}>Físico</option>
              </CSelect>
              <FieldErrors
                errors={getFieldErrors("documentType", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Copia física requerida:</CLabel>
            </CCol>
            <CCol>
              <CRow>
                <AnimatedCheckbox
                  value={editingDocument.physicalCopyRequired === true}
                  onChange={onPhysicalRequiredChange}
                ></AnimatedCheckbox>
              </CRow>

              <FieldErrors
                errors={
                  getFieldErrors("physicalCopyRequired", errors) as string[]
                }
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Fecha de Vencimiento:</CLabel>
            </CCol>
            <CCol>
              <Datetime
                className="pl-0"
                onChange={onExpiryDatehange}
                value={expiryDate}
                locale="es/PY"
                dateFormat="DD/MM/YYYY"
                timeFormat={false}
                closeOnSelect={true}
              ></Datetime>
              <FieldErrors
                errors={getFieldErrors("expirationDate", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Anticipación Vencimiento(días):</CLabel>
            </CCol>
            <CCol md={10}>
              <CInput
                type="number"
                placeholder="Días"
                step={1}
                value={
                  editingDocument.expirationNoticeDays !== undefined
                    ? editingDocument.expirationNoticeDays
                    : undefined
                }
                onChange={onNoticeDaysChange}
              ></CInput>
              <FieldErrors
                errors={
                  getFieldErrors("expirationNoticeDays", errors) as string[]
                }
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Tolerancia(días):</CLabel>
            </CCol>
            <CCol md={10}>
              <CInput
                type="number"
                placeholder="Días"
                step={1}
                value={
                  editingDocument.expirationGraceDays !== undefined
                    ? editingDocument.expirationGraceDays
                    : undefined
                }
                onChange={onGraceDaysChange}
              ></CInput>
              <FieldErrors
                errors={
                  getFieldErrors("expirationGraceDays", errors) as string[]
                }
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Repetición de Alarma de Vencimiento(días):</CLabel>
            </CCol>
            <CCol md={10}>
              <CInput
                type="number"
                placeholder="Días"
                step={1}
                value={
                  editingDocument.expirationReminderRepeatDays !== undefined
                    ? editingDocument.expirationReminderRepeatDays
                    : undefined
                }
                onChange={onRepeatDaysChange}
              ></CInput>
              <FieldErrors
                errors={
                  getFieldErrors(
                    "expirationReminderRepeatDays",
                    errors
                  ) as string[]
                }
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Tags:</CLabel>
            </CCol>
            <CCol>
              <TagsCell
                currentTags={tags}
                allTags={allTags}
                onTagsChange={handleTagsChange}
              />
              <FieldErrors
                errors={getFieldErrors("tags", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Archivo:</CLabel>
            </CCol>
            <CCol>
              <div
                {...getRootProps()}
                style={{
                  border: "2px dashed #cccccc",
                  padding: "20px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragActive ? "#f0f0f0" : "transparent",
                }}
              >
                <input {...getInputProps()} />
                {uploadedFile ? (
                  <p>{uploadedFile.name}</p>
                ) : (
                  <p>
                    Arrastra y suelta un archivo aquí, o haz clic para
                    seleccionar
                  </p>
                )}
              </div>
              <FieldErrors
                errors={getFieldErrors("file", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        {initialDocument?.id && (
          <CFormGroup>
            <CRow>
              <CCol md={2}></CCol>
              <CCol md={2}>
                <CLabel>
                  Actual:{" "}
                  <button
                    onClick={handleDownloadClick}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      color: "#007bff",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    {editingDocument.filePath}
                  </button>
                </CLabel>
              </CCol>
              <CCol></CCol>
            </CRow>
          </CFormGroup>
        )}
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default DocumentForm;
